import React, { useState } from 'react';
import { X, Search, Pill } from 'lucide-react';
import { ScrollArea } from '../ui/scroll-area';
import DRUG_FORMULARY from '../../constants/drugFormulary';

const CATEGORIES = Object.keys(DRUG_FORMULARY);

export default function DrugReferenceModal({ isOpen, onClose }) {
  const [activeCategory, setActiveCategory] = useState(CATEGORIES[0]);
  const [search, setSearch] = useState('');

  if (!isOpen) return null;

  const drugs = DRUG_FORMULARY[activeCategory] || [];
  const filtered = search.trim()
    ? Object.values(DRUG_FORMULARY).flat().filter(d =>
        d.drug.toLowerCase().includes(search.toLowerCase()) ||
        d.indication.toLowerCase().includes(search.toLowerCase())
      )
    : drugs;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" data-testid="drug-reference-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Pill className="w-5 h-5 text-emerald-700" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">Drug Dose Reference</h2>
              <p className="text-xs text-slate-400">OBGYN Formulary — tap any drug for details</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 pt-3 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              data-testid="drug-search-input"
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search drug or indication..."
              className="w-full pl-10 pr-4 py-2.5 text-sm rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400"
            />
          </div>
        </div>

        {/* Category Tabs */}
        {!search.trim() && (
          <div className="px-5 pb-2 overflow-x-auto">
            <div className="flex gap-1.5 min-w-max">
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  data-testid={`drug-cat-${cat.replace(/\s/g,'-').toLowerCase()}`}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                    activeCategory === cat
                      ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                      : 'bg-slate-100 text-slate-500 hover:bg-slate-200 border border-transparent'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Drug List */}
        <ScrollArea className="flex-1 px-5 pb-5">
          <div className="space-y-2 pt-2">
            {filtered.length === 0 ? (
              <p className="text-center text-sm text-slate-400 py-8">No drugs found</p>
            ) : (
              filtered.map((drug, idx) => (
                <DrugCard key={`${drug.drug}-${idx}`} drug={drug} />
              ))
            )}
          </div>
        </ScrollArea>

        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50">
          <p className="text-xs text-slate-400 text-center">Verify doses with current formulary. AI-generated reference — not a prescription.</p>
        </div>
      </div>
    </div>
  );
}

function DrugCard({ drug }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={`rounded-xl border transition-all cursor-pointer ${expanded ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-100 bg-white hover:border-slate-200'}`}
      onClick={() => setExpanded(!expanded)}
      data-testid={`drug-card-${drug.drug.replace(/\s/g,'-').toLowerCase()}`}
    >
      <div className="px-4 py-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-bold text-slate-900">{drug.drug}</p>
          <p className="text-xs text-slate-500 mt-0.5">{drug.indication}</p>
        </div>
        <span className="flex-shrink-0 text-xs font-mono font-bold text-emerald-700 bg-emerald-100 px-2 py-1 rounded-md">
          {drug.dose.length > 30 ? drug.dose.slice(0, 28) + '...' : drug.dose}
        </span>
      </div>
      {expanded && (
        <div className="px-4 pb-3 border-t border-emerald-100 pt-2 space-y-1.5">
          <div className="flex gap-2">
            <span className="text-xs font-semibold text-slate-500 w-14 flex-shrink-0">Dose:</span>
            <span className="text-xs text-slate-800 font-medium">{drug.dose}</span>
          </div>
          <div className="flex gap-2">
            <span className="text-xs font-semibold text-slate-500 w-14 flex-shrink-0">Route:</span>
            <span className="text-xs text-slate-800">{drug.route}</span>
          </div>
          <div className="flex gap-2">
            <span className="text-xs font-semibold text-slate-500 w-14 flex-shrink-0">Notes:</span>
            <span className="text-xs text-slate-700 leading-relaxed">{drug.notes}</span>
          </div>
        </div>
      )}
    </div>
  );
}
